// AtlasColumn.tsx — S1. The conversation + composer atlas-cockpit renders
// on every cockpit screen (as the fixed right column at >=900px, or inside
// AtlasSheet below 900px — see CockpitFrame). A1 gives this something to
// talk to; until then it renders honestly from AtlasChannel's
// `connected: false` and does nothing else.
//
// Deliberately no microphone anywhere in this file (decision `eb659c8d`,
// S1c: "voice is the device's"). The composer is a <textarea>, full stop.
import { useId } from 'preact/hooks';
import type { AtlasChannel, AtlasChannelItem, CockpitOccupant } from '../lib/atlasChannel';
import './AtlasColumn.css';

export interface AtlasColumnProps {
  channel: AtlasChannel;
  /** Who the column is a conversation WITH. 'atlas' (the default) is the
   * only one actually wired — a `builder:${string}` value (a future chunk's
   * reviewed-builder occupant) only swaps the heading and the disconnected
   * sentence's name; no other behaviour follows it, same disconnected
   * channel either way. */
  occupant?: CockpitOccupant;
  /** Present only inside AtlasSheet (the <900px bottom sheet) — renders a
   * close control next to the heading. Absent on the fixed column, which
   * has nothing to close. */
  onClose?: () => void;
}

const BUILDER_PREFIX = 'builder:';

/** H4a (coordinator, 2026-10-16) — a project's real title can diverge from
 * its slug title-cased (`voicestrip` -> "Voicestrip", `studio` ->
 * "Studio" — both wrong: the real titles are "VoiceStrip" and "Studio
 * Assist"). Keyed by the column's own stable `id` minus the
 * `project:` prefix, matching `DEFAULT_PERSONAL_COLUMNS`'s own titles
 * (core/src/cockpit/columns.ts) exactly — a duplicated constant rather
 * than an import, same tradeoff routes/route.ts's own `PROJECT_SCREEN_
 * LABEL` already makes (this leaf component has no path to core without
 * a larger refactor); pinned equal by AtlasColumn.test.ts. A slug not
 * listed here (the test fixture's synthetic `builder:studio-stays`, or a
 * later project before this map is updated) falls back to the generic
 * title-casing below — never a crash, never the bare slug. */
const BUILDER_PROJECT_TITLES: Readonly<Record<string, string>> = {
  rexx: 'Rexx',
  stays: 'Studio Stays',
  voicestrip: 'VoiceStrip',
  studio: 'Studio Assist',
};

/** H2 (COCKPIT-HOME-COLUMNS-SPEC.md §H2) — "the pane's header names the
 * occupant ('Atlas' / 'Rexx builder')": a project builder is always
 * "<Project> builder", its project's own title (BUILDER_PROJECT_TITLES
 * above), never the bare slug a `builder:${slug}` occupant carries. Falls
 * back to title-casing each hyphen/underscore-separated word of the slug
 * (so `builder:studio-stays` reads "Studio Stays builder") for any slug
 * this file does not have a title for. */
function occupantLabel(occupant: CockpitOccupant): string {
  if (occupant === 'atlas') return 'Atlas';
  const slug = occupant.slice(BUILDER_PREFIX.length);
  const known = BUILDER_PROJECT_TITLES[slug];
  if (known) return `${known} builder`;
  const name = slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return `${name || slug} builder`;
}

/** Same shape ChatThread.tsx's own `clockOf` already uses — a bare local
 * time, never a date, for a row the column keeps in view only briefly. */
function clockOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function renderItem(item: AtlasChannelItem) {
  // Defensive switch: a kind this file does not know about (added by a
  // later chunk before this one is updated to name it) renders as nothing,
  // never a throw — see atlasChannel.ts's header. The `default` branch's
  // `_never` assignment is the other half of that contract: a kind this
  // union DOES name but this switch forgot to handle fails typecheck here
  // instead of silently falling through to "render nothing".
  switch (item.kind) {
    case 'message':
      return (
        <div
          key={item.id}
          class={`atlas-col__bubble atlas-col__bubble--${item.from === 'you' ? 'you' : 'atlas'}`}
        >
          {item.text}
        </div>
      );
    case 'status':
      return (
        <p
          key={item.id}
          class={`atlas-col__status${item.tone === 'warn' ? ' atlas-col__status--warn' : ''}`}
        >
          {item.text}
        </p>
      );
    case 'receipt':
      return (
        <div key={item.id} class="atlas-col__receipt">
          <span>{item.text}</span>
          {item.undo ? (
            <button type="button" class="atlas-col__undo" onClick={item.undo.run}>
              {item.undo.label}
            </button>
          ) : null}
        </div>
      );
    // G2 — a question with two buttons. Both go dead once either is pressed; the chosen one stays marked.
    case 'ask':
      return (
        <div key={item.id} class="atlas-col__ask">
          <span>{item.text}</span>
          <span class="atlas-col__ask-buttons">
            {(
              [
                ['done', 'Mark it done'],
                ['delete', 'Delete'],
              ] as const
            ).map(([choice, label]) => (
              <button
                key={choice}
                type="button"
                class={`atlas-col__ask-btn${item.answered === choice ? ' atlas-col__ask-btn--chosen' : ''}`}
                aria-pressed={item.answered === choice}
                disabled={item.answered !== null || !item.answer}
                onClick={() => item.answer?.(choice)}
              >
                {label}
              </button>
            ))}
          </span>
        </div>
      );
    // H4a — a builder pane's own journal row (never a link: build item 5
    // of the brief — `{item.text}` is a plain JSX child, so even a
    // `<script>` in it renders as literal text, same as every other kind
    // here). `time`'s own `dateTime` carries the ISO value for anything
    // reading the DOM; the VISIBLE text is a local clock, same helper
    // ChatThread.tsx's own `clockOf` already uses.
    case 'action':
    case 'task_opened':
    case 'task_closed':
      return (
        <div key={item.id} class="atlas-col__feed-row">
          <span class="atlas-col__feed-text">{item.text}</span>
          <time class="atlas-col__feed-at" dateTime={item.at}>
            {clockOf(item.at)}
          </time>
        </div>
      );
    default: {
      const _never: never = item;
      void _never;
      return null;
    }
  }
}

export function AtlasColumn({ channel, occupant = 'atlas', onClose }: AtlasColumnProps) {
  const composerId = useId();
  const label = occupantLabel(occupant);
  const isBuilder = occupant.startsWith(BUILDER_PREFIX);
  // H2 — "shows a one-line status from … kind 'status'": the MOST RECENT
  // status item, held in the header rather than only scrolling past in the
  // thread below, so "what is it doing right now" is readable without
  // scrolling — the thread (renderItem's own 'status' branch, unchanged)
  // still keeps every status in its place in the history.
  const latestStatus = [...channel.items].reverse().find((it): it is AtlasChannelItem & { kind: 'status' } => it.kind === 'status');
  // H4a build item 4 — a builder occupant's own feed is action/
  // task_opened/task_closed rows ONLY: its `status` rows surface in the
  // header line above, never a second time in the scrolling body. Atlas's
  // own conversation (unchanged) keeps every kind inline, as it always has.
  const visibleItems = isBuilder ? channel.items.filter((it) => it.kind !== 'status') : channel.items;

  return (
    <div class="atlas-col">
      <div class="atlas-col__head">
        <div class="atlas-col__head-row">
          <h2 class="atlas-col__title">{label}</h2>
          {onClose ? (
            <button
              type="button"
              class="atlas-col__close"
              aria-label={`Close ${label}`}
              onClick={onClose}
            >
              ×
            </button>
          ) : null}
        </div>
        {latestStatus ? (
          <p class={`atlas-col__head-status${latestStatus.tone === 'warn' ? ' atlas-col__head-status--warn' : ''}`} role="status">
            {latestStatus.text}
          </p>
        ) : null}
      </div>

      <div class="atlas-col__thread" role="log" aria-label={`Conversation with ${label}`}>
        {visibleItems.length === 0 ? (
          <p class="atlas-col__empty">
            {!channel.connected
              ? `${label} isn't connected to the cockpit yet.`
              : isBuilder
                ? `Nothing from the ${label} yet.`
                : 'Nothing here yet.'}
          </p>
        ) : (
          visibleItems.map(renderItem)
        )}
      </div>

      {/* H4a build item 4 — nothing goes INTO a builder pane from the web
          this chunk: the composer is disabled outright, with the spec's
          own literal sentence, regardless of `channel.connected` (unlike
          Atlas's own composer just below, which is disabled only while
          disconnected). */}
      {isBuilder ? (
        <p class="atlas-col__composer-off">Sending to builders is off</p>
      ) : (
        <form
          class="atlas-col__composer"
          onSubmit={(event) => {
            event.preventDefault();
            if (channel.connected && channel.draft.trim()) channel.send(channel.draft);
          }}
        >
          <label class="atlas-col__label" htmlFor={composerId}>
            Message {label}
          </label>
          <textarea
            id={composerId}
            class="atlas-col__input"
            value={channel.draft}
            disabled={!channel.connected}
            placeholder={channel.connected ? `Message ${label}` : undefined}
            onInput={(event) => channel.setDraft((event.target as HTMLTextAreaElement).value)}
          />
          <button type="submit" class="atlas-col__send" disabled={!channel.connected}>
            Send
          </button>
        </form>
      )}
    </div>
  );
}
